import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import type { StatisticsPeriod } from './useUserStatisticsPeriod';

export interface RetakeAttempt {
  attempt_id: number; test_id: number; test_title: string;
  attempt_number: number; retake_number: number; completed_at: string;
  score: number; is_passed: boolean; time_spent_seconds: number | null;
  first_score: number; first_completed_at: string;
}
export interface EmployeeRetakes {
  user_id: number; full_name: string; department: string;
  retakes_count: number; tests_count: number; passed_count: number; failed_count: number;
  average_score: number; latest_completed_at: string; attempts: RetakeAttempt[];
}

export default function useRetakeStatistics() {
  const [period, setPeriod] = useState<StatisticsPeriod>(null);
  const [revision, setRevision] = useState(0);
  const [users, setUsers] = useState<EmployeeRetakes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const scope = useRef(0);
  const changePeriod = useCallback((next: StatisticsPeriod) => {
    scope.current += 1;
    setPeriod(next);
    setRevision(value => value + 1);
    setUsers([]);
    setError('');
    setLoading(true);
  }, []);
  useEffect(() => {
    const current = ++scope.current;
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get('/admin/statistics/retakes', {
      params: period ? { date_from: period.from, date_to: period.to } : {},
    }).then(response => {
      if (cancelled || current !== scope.current) return;
      if (!Array.isArray(response.data?.users)
          || !response.data.users.every((u: EmployeeRetakes) => Array.isArray(u.attempts))) {
        throw new Error('Некорректная статистика пересдач');
      }
      setUsers(response.data.users);
    }).catch(reason => {
      if (!cancelled && current === scope.current) {
        setError(getApiErrorMessage(reason, 'Не удалось загрузить статистику пересдач'));
      }
    }).finally(() => {
      if (!cancelled && current === scope.current) setLoading(false);
    });
    return () => { cancelled = true; scope.current += 1; };
  }, [period, revision]);
  return { period, changePeriod, users, loading, error };
}
